import type { ReactNode } from "react";

/**
 * AuthShell — the entry surfaces, A1–A5.
 *
 * 🔴 A5, the camera + parental-consent gate, is the most consequential screen
 * in the product: three SEPARATE consents (camera / retain progress / WhatsApp)
 * because they are three different legal bases (ADR-016).
 *
 * A1 also lives here, and A1 must work with NO READING on the child path
 * (wireframes.md A1) — which is why this shell imposes no text chrome of its
 * own beyond an optional brand mark.
 *
 * 🔴 WHY `min-h-dvh` HERE, WHEN KidShell AND DashboardShell USE `h-dvh`.
 *
 * design-system.md §1 requires 1366×768 with no scrollbar. This shell still
 * meets that — but it must not enforce it by clipping, because AuthShell is
 * the one shell that also renders on a phone: A3/A4 are the parent's phone-OTP
 * screens, and the parent tier is explicitly "30 seconds on a phone"
 * (information-architecture.md §2).
 *
 * So the rule is satisfied by CONTENT, not by overflow. A5's wireframe is a
 * TWO-COLUMN shape — the on-device privacy statement on the left, the three
 * consents on the right — and that is precisely what lets the most
 * content-heavy auth screen fit a laptop viewport. `aside` expresses it. On a
 * narrow phone the columns stack and the page grows, which is the right
 * trade: clipping a legal consent gate would be worse than scrolling it.
 */
export interface AuthShellProps {
  /** The logo or wordmark. Artwork, never re-drawn (design-system.md §10). */
  brand?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  /**
   * A5 needs the room; A2–A4 do not.
   *
   * ⚠️ `page` IS THE SITE HEADER'S OWN CONTAINER WIDTH (1240px), AND IT EXISTS
   * FOR ONE SCREEN. The Connect page renders `SiteHeader` above itself, so at
   * `wide` (896px) its two columns sat visibly narrower than the logo and nav
   * bar directly above them — the page read as a different, smaller document
   * hanging under the site's own chrome. Matching the header's max width is what
   * makes the two agree; it is not a licence to widen an auth form, which is
   * still `narrow` for the reason A2–A4 are.
   */
  width?: "narrow" | "wide" | "page";
  /**
   * The left column of A5's two-column shape (wireframes.md A5). Stacks above
   * `children` on narrow viewports. Omit for A1–A4, which are single-column.
   */
  aside?: ReactNode;
  children: ReactNode;
  /**
   * Does `children` get the white card, or the bare page?
   *
   * ⚠️ `card` IS THE DEFAULT AND EVERY EXISTING CALLER KEEPS IT. A2–A5 are forms
   * — a bounded white panel on warm canvas is what says "this is the thing you
   * fill in", and A5's consent gate depends on it.
   *
   * `bare` exists for a screen whose content IS the composition rather than
   * something sitting on one: the plans page is a two-column hero carrying its
   * own artwork and its own inner card, and wrapping that in a second card put a
   * white box around a white box.
   */
  surface?: "card" | "bare";
  /** Small print, links, the "use seated / keyboard mode" escape on A5. */
  footer?: ReactNode;
  /**
   * A top bar — the way back, and the language switch.
   *
   * 🔴 IT IS A SLOT, NOT BUILT IN, BECAUSE A1 MUST NOT HAVE ONE. The child's
   * screen has no chrome a four-year-old could press by accident, and this
   * shell's whole docblock rests on imposing no text chrome of its own. A2–A4
   * are adult screens and need both controls; passing them in keeps that a
   * decision each screen makes rather than one this file makes for all of them.
   *
   * ⚠️ PINNED TO THE TOP, SO IT DOES NOT JOIN THE CENTRED STACK. A back link
   * that floated just above the card would move with the card on every
   * viewport, and on a phone it would sit halfway down the screen.
   */
  nav?: ReactNode;
}

export function AuthShell({
  brand,
  title,
  subtitle,
  width = "narrow",
  aside,
  children,
  surface = "card",
  footer,
  nav,
}: AuthShellProps) {
  const card = "rounded-2xl border border-line bg-surface p-8 shadow-card";
  const panel = surface === "bare" ? "" : card;
  return (
    <div className="dash-text flex min-h-dvh flex-col bg-canvas font-dash text-ink">
      {/*
        The wordmark's own block sequence — teal · amber · green · magenta ·
        amber — as a 3px rule. It is the one element shared by the marketing
        page, A1 and every auth screen, so a parent who followed a link from the
        site can see they are still in the same product.
      */}
      <div aria-hidden="true" className="flex h-[3px] w-full shrink-0">
        {["bg-teal", "bg-sunshine", "bg-leaf", "bg-primary", "bg-sunshine"].map(
          (bg, i) => (
            <span key={`${bg}-${String(i)}`} className={`flex-1 ${bg}`} />
          ),
        )}
      </div>

      {nav ? <div className="shrink-0 px-6 py-5 lg:px-10">{nav}</div> : null}

      {/*
        `flex-1` + `justify-center` keeps the card optically centred in whatever
        room is left once the band and nav have taken theirs — rather than
        centring in the viewport and letting the chrome push it off-centre.
      */}
      {/*
        ⚠️ `relative z-10` SO A BACKGROUND CANNOT SWALLOW THE PAGE. A screen may
        paint a full-bleed decoration behind this — the plans page stands on the
        entry screen's hills — and those are `fixed z-0`. A positioned element at
        `z-0` still paints above static content in the same stacking context, so
        without this the hills covered the footer's small print.
      */}
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pt-2 pb-14">
        <div
          className={`flex w-full flex-col gap-6 ${
            {
              narrow: "max-w-[27.5rem]",
              wide: "max-w-4xl",
              page: "max-w-[1240px]",
            }[width]
          }`}
        >
          {brand ? <div className="flex justify-center">{brand}</div> : null}

          {(title || subtitle) && (
            <div className="flex flex-col gap-2 text-center">
              {title ? (
                <h1 className="kid-text font-kid text-kid-md font-extrabold">
                  {title}
                </h1>
              ) : null}
              {subtitle ? (
                <p className="text-dash-md text-ink-soft">{subtitle}</p>
              ) : null}
            </div>
          )}

          {aside ? (
            // Two columns on a laptop, stacked on a phone — the A5 shape.
            <div className="grid gap-6 md:grid-cols-2">
              <div className="text-dash-md text-ink-soft">{aside}</div>
              <div className={card}>{children}</div>
            </div>
          ) : (
            <div className={panel}>{children}</div>
          )}

          {footer ? (
            <div className="mx-auto max-w-[44ch] text-center text-dash-sm text-muted">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default AuthShell;
